import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "../common/Modal";
import { deviceService } from "../../api/services/deviceService";
import { copyToClipboard } from "../../utils/clipboardUtils";

interface UnassignDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The device being released. */
  deviceId: string;
  /** Name of the screen it is currently linked to (for the confirmation copy). */
  screenName: string;
  /** The device's current code (shown in the warning copy). */
  currentCode?: string | null;
  /**
   * Optional hook for caller-specific work after a successful unassign. The
   * screen and device lists are refreshed by this modal, so callers don't need
   * to pass anything just to refetch them.
   */
  onUnassigned?: () => void;
}

/**
 * Confirm-and-execute flow for releasing a device from its screen.
 * On success it shows the device's freshly minted verification code so support
 * can relay it to whoever re-pairs the box — once this modal closes, the code
 * is only visible on the device itself.
 *
 * ANDROID ONLY. Both call sites hide the action for Raspberry Pi players and
 * the backend rejects the mutation for them; a Pi is swapped by editing the
 * screen and entering the replacement device's code.
 */
export default function UnassignDeviceModal({
  isOpen,
  onClose,
  deviceId,
  screenName,
  currentCode,
  onUnassigned,
}: UnassignDeviceModalProps) {
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const queryClient = useQueryClient();

  const handleUnassign = async () => {
    if (!deviceId) return;
    setIsSubmitting(true);
    try {
      const result = await deviceService.unassignDevice(
        deviceId,
        note.trim() || undefined,
      );
      setNewCode(result.verificationCode);
      setIsDone(true);
      toast.success("Device unassigned from screen");
      // An unassign changes both sides of Screen Management — the screen loses
      // its device code, the device loses its screen — and this modal is opened
      // from either tab. Refresh both so whichever tab isn't in front doesn't
      // keep showing the old pairing.
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["screens"] });
      onUnassigned?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to unassign device");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    if (!newCode) return;
    if (await copyToClipboard(newCode)) {
      toast.info("Code copied to clipboard");
    } else {
      toast.error("Couldn't copy the code — please select and copy it manually");
    }
  };

  const handleClose = () => {
    setNote("");
    setNewCode(null);
    setIsDone(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isDone ? "Device Unassigned" : "Unassign Device"}
      size="md"
    >
      {!isDone ? (
        <div>
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-gray-800 mb-1">
                This screen will go dark.
              </p>
              <p>
                The device currently linked to{" "}
                <span className="font-semibold">{screenName}</span>
                {currentCode && (
                  <>
                    {" "}
                    (code{" "}
                    <span className="font-mono font-semibold">
                      {currentCode}
                    </span>
                    )
                  </>
                )}{" "}
                will stop playing immediately and return to the pairing screen.
                The screen stays configured and can accept a replacement
                device.
              </p>
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. device swapped for repair"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          />

          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleUnassign}
              disabled={isSubmitting || !deviceId}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
            >
              {isSubmitting ? "Unassigning…" : "Unassign Device"}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-700 mb-1">
            The device has been released from{" "}
            <span className="font-semibold">{screenName}</span>.
          </p>
          {newCode ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Its verification code is now:
              </p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-2xl font-mono font-bold tracking-widest bg-gray-100 px-4 py-2 rounded-lg select-all">
                  {newCode}
                </span>
                <button
                  type="button"
                  onClick={handleCopyCode}
                  className="p-2 text-gray-500 hover:text-gray-700"
                  title="Copy code"
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                The same code is shown on the device's pairing screen. Use it
                to pair the device to another screen.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500 mb-4">
              The device will show its pairing code on screen.
            </p>
          )}
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-primary-default hover:bg-primary-light text-white text-sm font-medium rounded-lg"
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  );
}
